/**
 * Delegation-service API — typed client for GRANULAR cross-account delegation.
 *
 * Gateway prefix `/api/v1/delegations` (api-gateway get_service_routes). Lets an
 * account owner grant another PERSON (identified by email, who keeps their own
 * separate account) a precisely-scoped right to act inside the owner's account:
 * e.g. "peut publier sur Facebook mais PAS sur LinkedIn", "peut créer des
 * publicités". The grantee sees ONLY what was granted, never the whole account.
 *
 * Cross-account requests (made while a managed account is selected in the
 * header switcher) carry `X-Acting-Tenant: <owner_tenant_id>` — attached
 * globally by the axios/fetch client (see lib/api/client.ts `setActingTenantId`
 * + the ActingTenantProvider). The backend re-checks every grant on every call;
 * this client never bypasses it.
 *
 * Loi-25 / CASL: a grant is an explicit, auditable, time-boxable and instantly
 * revocable authorization to act on another organization's data. The UI
 * (DelegationsPage) surfaces that wording; the server is authoritative.
 */

import { api } from '@/lib/api/client';
import { tr } from '@/i18n';

const BASE = '/api/v1/delegations';

// ---------------------------------------------------------------------------
// Platforms in scope for per-platform publish/ads scoping. Reuses the app's
// existing platform display metadata (routes/publishing/format.ts PLATFORM_META
// via `delegationPlatformMeta` below) and supplements the few keys that catalog
// does not carry yet (threads/pinterest/bluesky). `x` is the canonical key for
// X / Twitter here (the owner's product vocabulary); it maps to the app's
// `twitter` meta for the icon/label.
// ---------------------------------------------------------------------------
export const DELEGATION_PLATFORMS = [
  'facebook',
  'instagram',
  'linkedin',
  'youtube',
  'tiktok',
  'threads',
  'pinterest',
  'bluesky',
  'x',
] as const;
export type DelegationPlatform = (typeof DELEGATION_PLATFORMS)[number];

// ---------------------------------------------------------------------------
// Resources & actions in scope (mirror the backend delegation vocabulary).
// ---------------------------------------------------------------------------

/** A delegatable resource. */
export const DELEGATION_RESOURCES = ['publish', 'ads'] as const;
export type DelegationResource = (typeof DELEGATION_RESOURCES)[number];

/** Actions available per resource. */
export const RESOURCE_ACTIONS: Record<DelegationResource, readonly string[]> = {
  publish: ['create', 'edit', 'delete', 'boost'],
  ads: ['create', 'boost'],
};

/** French labels for resources / actions, for permission summaries. */
export const RESOURCE_LABELS: Record<DelegationResource, string> = {
  publish: tr('shell.lib.apiDelegations.publish'),
  ads: tr('shell.lib.apiDelegations.ads'),
};

export const ACTION_LABELS: Record<string, string> = {
  create: tr('shell.lib.apiDelegations.create'),
  edit: tr('shell.lib.apiDelegations.edit'),
  delete: tr('shell.lib.apiDelegations.delete'),
  boost: tr('shell.lib.apiDelegations.boost'),
};

/**
 * True when a resource is scoped per social platform. Only `publish` is
 * per-platform (Facebook ✓ / LinkedIn ✗). `ads` is coarse — granting an ads
 * action covers every ad provider (the ad model's platform is the provider
 * `meta`/`google`, a different vocabulary; a social-platform picker there would
 * never match). Ads grants are therefore emitted with `platform: null`.
 */
export function isPlatformScopedResource(resource: string): boolean {
  return resource === 'publish';
}

// ---------------------------------------------------------------------------
// Domain types (mirror the backend contract)
// ---------------------------------------------------------------------------

export type GrantEffect = 'allow' | 'deny';
export type GrantStatus = 'pending' | 'active' | 'revoked';

/** One granular permission line in a grant. `platform` null = all platforms. */
export interface DelegationPermission {
  resource: string;
  action: string;
  platform: string | null;
  effect: GrantEffect;
}

/** A delegation grant (issued or received). */
export interface Grant {
  id: string;
  owner_tenant_id: string;
  grantee_email: string;
  grantee_user_id: string | null;
  grantee_tenant_id: string | null;
  status: GrantStatus;
  expires_at: string | null;
  label: string | null;
  permissions: DelegationPermission[];
  created_at: string;
  updated_at: string;
}

/** The `GET /delegations` envelope. */
export interface GrantsEnvelope {
  issued: Grant[];
  received: Grant[];
}

/** An account the current user may act in (from `GET /delegations/accounts`). */
export interface DelegatedAccount {
  owner_tenant_id: string;
  /** Optional on the grant — the backend returns null when no label was set. */
  label: string | null;
  expires_at: string | null;
  permissions: DelegationPermission[];
}

/** Display name for a managed account: label, else a short owner-id fallback. */
export function delegatedAccountName(acc: Pick<DelegatedAccount, 'label' | 'owner_tenant_id'>): string {
  const label = acc.label?.trim();
  return label || tr('shell.lib.apiDelegations.delegatedAccountV', { v: acc.owner_tenant_id.slice(0, 8) });
}

/** Create-grant payload (`POST /delegations`). */
export interface CreateGrantInput {
  grantee_email: string;
  permissions: DelegationPermission[];
  expires_at?: string | null;
  label?: string | null;
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

/** `POST /delegations` — grant a person scoped access to the caller's account. */
export function createGrant(input: CreateGrantInput): Promise<Grant> {
  return api.post<Grant>(BASE, {
    grantee_email: input.grantee_email,
    permissions: input.permissions,
    expires_at: input.expires_at ?? null,
    label: input.label ?? null,
  });
}

/** `GET /delegations` — grants the caller issued + grants they received. */
export function listGrants(): Promise<GrantsEnvelope> {
  return api.get<GrantsEnvelope>(BASE);
}

/** `POST /delegations/{id}/accept` — accept a pending grant received by email. */
export function acceptGrant(id: string): Promise<Grant> {
  return api.post<Grant>(`${BASE}/${encodeURIComponent(id)}/accept`);
}

/** `DELETE /delegations/{id}` — revoke (issuer) or decline/leave (grantee). 204. */
export function revokeGrant(id: string): Promise<void> {
  return api.delete<void>(`${BASE}/${encodeURIComponent(id)}`);
}

/** `GET /delegations/accounts` — accounts the current user may act in. */
export function listDelegatedAccounts(): Promise<DelegatedAccount[]> {
  return api.get<DelegatedAccount[]>(`${BASE}/accounts`);
}

// ---------------------------------------------------------------------------
// Presentation helpers (pure)
// ---------------------------------------------------------------------------

/** Human label for one permission line (e.g. "Publier · Facebook (autorisé)"). */
export function permissionLabel(perm: DelegationPermission): string {
  const resource = RESOURCE_LABELS[perm.resource as DelegationResource] ?? perm.resource;
  const action = ACTION_LABELS[perm.action] ?? perm.action;
  const platform = perm.platform ? ` · ${perm.platform}` : tr('shell.lib.apiDelegations.allPlatforms');
  const effect = perm.effect === 'deny' ? tr('shell.lib.apiDelegations.denied') : '';
  return `${resource} — ${action}${platform}${effect}`;
}
